import type {
  ApiCompetition,
  CompetitionCategory,
  CompetitionSystemCode,
  CompetitionTypeCode,
  CompetitionVisibilityCode,
  CreateCompetitionRequest,
  DisciplineCode,
  MatchTypeCode,
  SecondPhaseTypeCode,
  UpdateCompetitionPayload,
} from './types';

/**
 * Model formularza tworzenia rozgrywki.
 *
 * Pola odpowiadają 1:1 rekordowi `CreateCompetitionRequest` z `common-lib` —
 * formularz nie ma własnych pojęć, których backend nie zna. Wartości liczbowe
 * trzymamy jako napisy, bo tak zwracają je kontrolki, a konwersję robi
 * `buildCreateCompetitionPayload`.
 */
export interface TournamentDraft {
  name: string;
  description: string;
  discipline: DisciplineCode;
  type: CompetitionTypeCode;
  system: CompetitionSystemCode;
  matchType: MatchTypeCode;
  category: CompetitionCategory;
  visibility: CompetitionVisibilityCode;
  maxPlayers: string;
  /** Szacowany czas meczu w minutach — długość bloku meczu na kalendarzu kortów. */
  estimatedMatchDurationMinutes: string;
  startDate: string;
  endDate: string;
  thirdPlaceMatch: boolean;
  promotions: boolean;
  relegations: boolean;
  /**
   * Poniższe pola dotyczą wyłącznie systemu GROUP_PLAYOFF.
   * `playoffSize` nie jest polem formularza — wylicza je `planGroupPhase`.
   */
  groupSize: string;
  advancingPerGroup: string;
  /** Czy poza miejscami z grup awansują też najlepsi z kolejnego miejsca. */
  extraAdvancing: boolean;
  /** Ilu najlepszych z kolejnego miejsca awansuje cross-group (gdy włączone). */
  extraQualifiers: string;
  secondPhaseType: SecondPhaseTypeCode;
  /** Zasady punktacji meczu (`scoringRules`). */
  bestOf: string;
  superTiebreak: boolean;
  noAd: boolean;
  /** Pusty napis oznacza sety standardowe (do 6 gemów). */
  shortSetTarget: string;
}

/** Pusty formularz — bez podpowiedzianej nazwy i dat. */
export const INITIAL_DRAFT: TournamentDraft = {
  name: '',
  description: '',
  discipline: 'TENNIS',
  type: 'TOURNAMENT',
  system: 'PLAYOFF',
  matchType: 'SINGLES',
  category: 'OPEN',
  visibility: 'PUBLIC',
  maxPlayers: '32',
  estimatedMatchDurationMinutes: '60',
  startDate: '',
  endDate: '',
  thirdPlaceMatch: true,
  promotions: false,
  relegations: false,
  groupSize: '4',
  advancingPerGroup: '2',
  extraAdvancing: false,
  extraQualifiers: '1',
  secondPhaseType: 'PLAYOFF',
  bestOf: '3',
  superTiebreak: true,
  noAd: false,
  shortSetTarget: '',
};

/* --- Listy wyboru — wprost z enumów API --- */

export const DISCIPLINE_OPTIONS: readonly DisciplineCode[] = [
  'TENNIS',
  'PADEL',
  'SQUASH',
  'PICKLEBALL',
  'BADMINTON',
  'FITNESS',
];

export const COMPETITION_TYPE_OPTIONS: readonly CompetitionTypeCode[] = ['TOURNAMENT', 'LEAGUE'];

/**
 * Systemy dopuszczalne dla danego rodzaju rozgrywki.
 *
 * Podział jest decyzją produktową, nie ograniczeniem API — backend przyjmie
 * każdą kombinację. Turniej rozgrywa się w jednym terminie, więc dostaje
 * drabinkę i grupy; liga toczy się przez sezon, więc dostaje tabelę i drabinkę
 * wyzwań.
 */
const SYSTEMS_BY_TYPE: Record<CompetitionTypeCode, readonly CompetitionSystemCode[]> = {
  TOURNAMENT: ['PLAYOFF', 'GROUP_PLAYOFF', 'SINGLE_GROUP'],
  LEAGUE: ['SINGLE_GROUP', 'CHALLENGE'],
};

/** Systemy do wyboru przy danym rodzaju rozgrywki. */
export function systemOptionsFor(type: CompetitionTypeCode): readonly CompetitionSystemCode[] {
  return SYSTEMS_BY_TYPE[type];
}

/**
 * Zmienia rodzaj rozgrywki i pilnuje, żeby system pozostał dopuszczalny.
 *
 * Bez tego przełączenie turnieju z drabinką na ligę zostawiłoby w formularzu
 * system, którego lista już nie pokazuje — i wysłałoby go do API.
 */
export function withCompetitionType(
  draft: TournamentDraft,
  type: CompetitionTypeCode,
): TournamentDraft {
  const allowed = systemOptionsFor(type);
  const system = allowed.includes(draft.system) ? draft.system : allowed[0]!;
  return { ...draft, type, system };
}

export const MATCH_TYPE_OPTIONS: readonly MatchTypeCode[] = ['SINGLES', 'DOUBLES', 'MIXT'];

export const CATEGORY_OPTIONS: readonly CompetitionCategory[] = [
  'OPEN',
  'PRO',
  'PROAM',
  'AMATEUR',
  'JUNIOR',
  'KIDS',
];

export const VISIBILITY_OPTIONS: readonly CompetitionVisibilityCode[] = ['PUBLIC', 'PRIVATE'];

export const SECOND_PHASE_OPTIONS: readonly SecondPhaseTypeCode[] = [
  'PLAYOFF',
  'FINAL_GROUP',
  'CLASSIFICATION_GROUPS',
];

/**
 * Dozwolone wartości `bestOf`.
 *
 * Value object `ScoringRules` po stronie serwera przyjmuje wyłącznie 1, 3
 * albo 5 — inna liczba kończy się błędem 400, więc nie dajemy jej wybrać.
 * `1` to mecz rozstrzygany jednym pełnym setem (krótkie formaty klubowe).
 */
export const BEST_OF_OPTIONS = ['1', '3', '5'] as const;

/**
 * Czy przy tym `bestOf` super tie-break ma sens. Przy jednym secie decydujący
 * set to jedyny set — włączony super tie-break zamieniłby cały mecz w tie-break
 * do 10, a nie o to chodzi w formacie „jeden pełny set".
 */
export function allowsSuperTiebreak(bestOf: string): boolean {
  return bestOf !== '1';
}

/* --- Faza grupowa --- */

/** Wyliczony kształt fazy grupowej i wynikającej z niej drabinki finałowej. */
export interface GroupPhasePlan {
  /** Liczba grup przy zadanym limicie zawodników i liczebności grupy. */
  groups: number;
  /** Ilu zawodników wychodzi z grup łącznie (razem z dalszymi miejscami). */
  qualifiers: number;
  /** Ilu z kwalifikantów to najlepsi z kolejnego miejsca (0 = wyłączone). */
  extra: number;
  /** Wielkość drabinki finałowej — potęga dwójki, jak wymaga walidator API. */
  playoffSize: number;
  /** Ilu kwalifikantów nie zmieści się w drabince i odpadnie. */
  dropped: number;
}

/**
 * Wylicza fazę grupową z limitu zawodników, liczebności grupy i liczby awansujących.
 *
 * `playoffSize` to po stronie serwera cap drabinki finałowej: gdy z grup wyjdzie
 * więcej zawodników, odpadają najsłabsi kwalifikanci cross-group. Bierzemy więc
 * największą potęgę dwójki, która mieści się w liczbie kwalifikantów — drabinka
 * wychodzi pełna, bez wolnych losów.
 *
 * Zwraca `null`, gdy dane nie pozwalają rozstawić grup (walidator API i tak
 * odrzuciłby takie żądanie).
 */
export function planGroupPhase(draft: TournamentDraft): GroupPhasePlan | null {
  const maxPlayers = Number(draft.maxPlayers);
  const groupSize = Number(draft.groupSize);
  const advancing = Number(draft.advancingPerGroup);

  if (!Number.isInteger(maxPlayers) || maxPlayers < 2) return null;
  if (!Number.isInteger(groupSize) || groupSize < 2) return null;
  if (!Number.isInteger(advancing) || advancing < 1 || advancing >= groupSize) return null;

  const groups = Math.ceil(maxPlayers / groupSize);

  // Awans z dalszych miejsc: tylko kolejne miejsce, więc maks. jeden na grupę.
  // Wartości spoza zakresu przycinamy na potrzeby podglądu — dokładny komunikat
  // daje `validateDraft`.
  const extraRaw = Number(draft.extraQualifiers);
  const extra = draft.extraAdvancing
    ? Number.isInteger(extraRaw) && extraRaw >= 1
      ? Math.min(extraRaw, groups)
      : 0
    : 0;

  const qualifiers = groups * advancing + extra;
  if (qualifiers < 2) return null;

  // Mnożenie zamiast Math.log2 — potęgi liczymy dokładnie, bez błędu zmiennoprzecinkowego.
  let playoffSize = 1;
  while (playoffSize * 2 <= qualifiers) playoffSize *= 2;

  return { groups, qualifiers, extra, playoffSize, dropped: qualifiers - playoffSize };
}

/** Etykiety pól, które nie mają odpowiednika w `labels.ts`. */
export const VISIBILITY_LABELS: Record<CompetitionVisibilityCode, string> = {
  PUBLIC: 'Publiczna',
  PRIVATE: 'Prywatna',
};

export const SECOND_PHASE_LABELS: Record<SecondPhaseTypeCode, string> = {
  PLAYOFF: 'Faza pucharowa',
  FINAL_GROUP: 'Grupa finałowa',
  CLASSIFICATION_GROUPS: 'Grupy klasyfikacyjne',
};

/** Opis wyboru `bestOf` — „do 2 wygranych setów (best of 3)". */
export const BEST_OF_LABELS: Record<string, string> = {
  '1': 'Jeden pełny set (best of 1)',
  '3': 'Do 2 wygranych setów (best of 3)',
  '5': 'Do 3 wygranych setów (best of 5)',
};

export const SHORT_SET_OPTIONS = ['', '4'] as const;

export const SHORT_SET_LABELS: Record<string, string> = {
  '': 'Standardowe (do 6 gemów)',
  '4': 'Skrócone (do 4 gemów)',
};

/* --- Zależności między polami --- */

/** Czy rozgrywka ma fazę pucharową — tylko wtedy mecz o 3. miejsce ma sens. */
export function hasBracketPhase(draft: TournamentDraft): boolean {
  if (draft.system === 'PLAYOFF') return true;
  return draft.system === 'GROUP_PLAYOFF' && draft.secondPhaseType === 'PLAYOFF';
}

/**
 * Czy awanse i spadki mają sens dla tej rozgrywki.
 *
 * Wyłącznie dla ligi: awans i spadek to ruch między dywizjami w sezonie,
 * a turniej rozgrywa się w jednym terminie i nie ma dokąd nikogo przenieść —
 * nawet gdy toczy się systemem „każdy z każdym" i prowadzi tabelę.
 */
export function hasPromotionRules(draft: TournamentDraft): boolean {
  return draft.type === 'LEAGUE';
}

/** Czy pokazać ustawienia fazy grupowej. */
export function hasGroupPhase(draft: TournamentDraft): boolean {
  return draft.system === 'GROUP_PLAYOFF';
}

/**
 * Czy ustawienia awansu (liczba awansujących, dalsze miejsca, cap drabinki)
 * mają zastosowanie. W grupach klasyfikacyjnych nikt nie odpada — każde
 * miejsce z grup tworzy własną grupę drugiej fazy, więc selekcja nie istnieje.
 */
export function hasAdvancementSettings(draft: TournamentDraft): boolean {
  return hasGroupPhase(draft) && draft.secondPhaseType !== 'CLASSIFICATION_GROUPS';
}

/**
 * Podgląd fazy grupowej dla grup klasyfikacyjnych — tylko liczba grup,
 * bo kwalifikanci i drabinka nie istnieją. `null`, gdy dane niepełne.
 */
export function planClassificationPhase(draft: TournamentDraft): { groups: number } | null {
  const maxPlayers = Number(draft.maxPlayers);
  const groupSize = Number(draft.groupSize);
  if (!Number.isInteger(maxPlayers) || maxPlayers < 2) return null;
  if (!Number.isInteger(groupSize) || groupSize < 2) return null;
  return { groups: Math.ceil(maxPlayers / groupSize) };
}

/* --- Walidacja --- */

/** Zwraca komunikat błędu albo `null`, gdy formularz da się wysłać. */
export function validateDraft(draft: TournamentDraft): string | null {
  if (!draft.name.trim()) {
    return 'Podaj nazwę rozgrywki.';
  }
  const maxPlayers = Number(draft.maxPlayers);
  if (!Number.isInteger(maxPlayers) || maxPlayers < 2) {
    return 'Liczba zawodników musi wynosić co najmniej 2.';
  }
  if (draft.startDate && draft.endDate && draft.endDate < draft.startDate) {
    return 'Data zakończenia nie może być wcześniejsza niż data rozpoczęcia.';
  }
  // Zakres jak w walidacji domeny (Competition.validateMatchDuration).
  const matchDuration = Number(draft.estimatedMatchDurationMinutes);
  if (!Number.isInteger(matchDuration) || matchDuration < 5 || matchDuration > 480) {
    return 'Szacowany czas meczu musi mieścić się w przedziale 5–480 minut.';
  }
  if (hasGroupPhase(draft)) {
    const groupSize = Number(draft.groupSize);
    if (!Number.isInteger(groupSize) || groupSize < 2) {
      return 'Liczebność grupy musi wynosić co najmniej 2.';
    }
    // Grupy klasyfikacyjne nie mają selekcji awansujących — pola awansu pomijamy.
    if (!hasAdvancementSettings(draft)) {
      return null;
    }
    const advancing = Number(draft.advancingPerGroup);
    if (!Number.isInteger(advancing) || advancing < 1 || advancing >= groupSize) {
      return 'Z grupy musi awansować mniej zawodników, niż grupa liczy.';
    }
    if (draft.extraAdvancing) {
      const extra = Number(draft.extraQualifiers);
      if (!Number.isInteger(extra) || extra < 1) {
        return 'Liczba awansujących z dalszych miejsc musi wynosić co najmniej 1.';
      }
      // Awansować może tylko kolejne miejsce — maks. jeden zawodnik na grupę.
      const groups = Math.ceil(maxPlayers / groupSize);
      if (extra > groups) {
        return 'Z kolejnego miejsca może awansować co najwyżej tylu zawodników, ile jest grup.';
      }
    }
  }
  return null;
}

/** Zamienia napis z kontrolki na liczbę; pusty napis daje `null`. */
function optionalNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Składa żądanie `POST /api/competitions` z formularza i kontekstu klubu.
 *
 * Klub bierzemy z przełącznika w pasku górnym — rozgrywkę tworzy się zawsze
 * w jednym klubie, więc jest jednocześnie organizatorem (`clubId`) i miejscem
 * gry (`venueClubId`). Bez `clubId` backend ustawiłby status AWAITING_PAYMENT
 * zamiast DRAFT.
 */
export function buildCreateCompetitionPayload(
  draft: TournamentDraft,
  club: { id: string; city: string | null },
): CreateCompetitionRequest {
  const city = club.city?.trim() ?? '';

  return {
    name: draft.name.trim(),
    description: draft.description.trim() || null,
    type: draft.type,
    discipline: draft.discipline,
    matchType: draft.matchType,
    system: draft.system,
    category: draft.category,
    visibility: draft.visibility,
    // Zasięg miejski wymaga miasta — klub bez adresu opisujemy jako ogólnopolski,
    // inaczej backend odrzuci żądanie.
    scope: city ? 'CITY' : 'NATIONAL',
    city: city || null,
    clubId: club.id,
    venueClubId: club.id,
    scoringRules: {
      bestOf: Number(draft.bestOf),
      // Przy jednym secie super tie-break zamieniłby cały mecz w tie-break
      // do 10 — wymuszamy pełny set niezależnie od stanu przełącznika.
      superTiebreak: allowsSuperTiebreak(draft.bestOf) ? draft.superTiebreak : false,
      noAd: draft.noAd,
      shortSetTarget: optionalNumber(draft.shortSetTarget),
    },
    maxPlayers: Number(draft.maxPlayers),
    estimatedMatchDurationMinutes: optionalNumber(draft.estimatedMatchDurationMinutes),
    // Ustawienia fazy grupowej wysyłamy wyłącznie dla GROUP_PLAYOFF —
    // `playoffSize` przy innym systemie kończy się błędem 400. Ustawienia awansu
    // dodatkowo tylko poza grupami klasyfikacyjnymi (tam nikt nie odpada).
    groupSize: hasGroupPhase(draft) ? optionalNumber(draft.groupSize) : null,
    advancingPerGroup: hasAdvancementSettings(draft)
      ? optionalNumber(draft.advancingPerGroup)
      : null,
    extraQualifiers:
      hasAdvancementSettings(draft) && draft.extraAdvancing
        ? optionalNumber(draft.extraQualifiers)
        : null,
    secondPhaseType: hasGroupPhase(draft) ? draft.secondPhaseType : null,
    playoffSize: hasAdvancementSettings(draft)
      ? (planGroupPhase(draft)?.playoffSize ?? null)
      : null,
    thirdPlaceMatch: hasBracketPhase(draft) && draft.thirdPlaceMatch,
    promotions: hasPromotionRules(draft) && draft.promotions,
    relegations: hasPromotionRules(draft) && draft.relegations,
    startDate: draft.startDate || null,
    endDate: draft.endDate || null,
  };
}

/* --- Edycja rozgrywki --- */

/**
 * Buduje formularz z istniejącej rozgrywki — odwrotność
 * `buildCreateCompetitionPayload`. Braki (np. kategoria `null`) dostają
 * wartości domyślne formularza, bo kontrolki nie mają stanu „brak".
 */
export function draftFromCompetition(competition: ApiCompetition): TournamentDraft {
  return {
    name: competition.name,
    description: competition.description ?? '',
    discipline: competition.discipline,
    type: competition.type,
    system: competition.system,
    matchType: competition.matchType,
    category: competition.category ?? INITIAL_DRAFT.category,
    visibility: competition.visibility,
    maxPlayers: String(competition.maxPlayers),
    estimatedMatchDurationMinutes: String(
      competition.estimatedMatchDurationMinutes ?? INITIAL_DRAFT.estimatedMatchDurationMinutes,
    ),
    startDate: competition.startDate ?? '',
    endDate: competition.endDate ?? '',
    thirdPlaceMatch: competition.thirdPlaceMatch,
    promotions: competition.promotions,
    relegations: competition.relegations,
    groupSize:
      competition.groupSize !== null ? String(competition.groupSize) : INITIAL_DRAFT.groupSize,
    advancingPerGroup:
      competition.advancingPerGroup !== null
        ? String(competition.advancingPerGroup)
        : INITIAL_DRAFT.advancingPerGroup,
    extraAdvancing: (competition.extraQualifiers ?? 0) > 0,
    extraQualifiers:
      competition.extraQualifiers !== null && competition.extraQualifiers > 0
        ? String(competition.extraQualifiers)
        : INITIAL_DRAFT.extraQualifiers,
    secondPhaseType: competition.secondPhaseType ?? INITIAL_DRAFT.secondPhaseType,
    bestOf: String(competition.scoringRules?.bestOf ?? INITIAL_DRAFT.bestOf),
    superTiebreak: competition.scoringRules?.superTiebreak ?? INITIAL_DRAFT.superTiebreak,
    noAd: competition.scoringRules?.noAd ?? INITIAL_DRAFT.noAd,
    shortSetTarget:
      competition.scoringRules?.shortSetTarget != null
        ? String(competition.scoringRules.shortSetTarget)
        : '',
  };
}

/**
 * Składa żądanie `PUT /api/competitions/{id}` z formularza edycji.
 *
 * Lokalizacji i rozliczeń nie ruszamy (`null` = backend zachowa dotychczasowe
 * wartości): edycja ustawień nie powinna po cichu przenosić rozgrywki do
 * innego klubu ani zmieniać cennika.
 */
export function buildUpdateCompetitionPayload(draft: TournamentDraft): UpdateCompetitionPayload {
  return {
    name: draft.name.trim(),
    description: draft.description.trim(),
    type: draft.type,
    discipline: draft.discipline,
    matchType: draft.matchType,
    system: draft.system,
    category: draft.category,
    visibility: draft.visibility,
    scope: null,
    city: null,
    venueClubId: null,
    scoringRules: {
      bestOf: Number(draft.bestOf),
      superTiebreak: allowsSuperTiebreak(draft.bestOf) ? draft.superTiebreak : false,
      noAd: draft.noAd,
      shortSetTarget: optionalNumber(draft.shortSetTarget),
    },
    pointRules: null,
    entryFee: null,
    maxPlayers: Number(draft.maxPlayers),
    estimatedMatchDurationMinutes: optionalNumber(draft.estimatedMatchDurationMinutes),
    // Jak przy tworzeniu: ustawienia fazy grupowej tylko dla GROUP_PLAYOFF —
    // `playoffSize` przy innym systemie odrzuca walidacja domeny. Ustawienia
    // awansu tylko poza grupami klasyfikacyjnymi.
    groupSize: hasGroupPhase(draft) ? optionalNumber(draft.groupSize) : null,
    advancingPerGroup: hasAdvancementSettings(draft)
      ? optionalNumber(draft.advancingPerGroup)
      : null,
    // 0 wyłącza awans z dalszych miejsc — `null` w edycji oznacza „bez zmian",
    // więc odznaczenie przełącznika musi jawnie wyzerować ustawienie.
    extraQualifiers:
      hasAdvancementSettings(draft) && draft.extraAdvancing
        ? optionalNumber(draft.extraQualifiers)
        : 0,
    secondPhaseType: hasGroupPhase(draft) ? draft.secondPhaseType : null,
    playoffSize: hasAdvancementSettings(draft)
      ? (planGroupPhase(draft)?.playoffSize ?? null)
      : null,
    thirdPlaceMatch: hasBracketPhase(draft) && draft.thirdPlaceMatch,
    promotions: hasPromotionRules(draft) && draft.promotions,
    relegations: hasPromotionRules(draft) && draft.relegations,
    startDate: draft.startDate || null,
    endDate: draft.endDate || null,
  };
}
